import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, PenLine, CheckCircle2, X, Eraser, Layers } from "lucide-react";
import ComplianceValidation from "@/components/ComplianceValidation";

const QUARTERS = [1, 2, 3, 4];
const METHOD_LABEL = {
  e_file_csv: "E-Filed (CSV upload)",
  signable_pdf: "Signed PDF",
  mailed: "Mailed",
  portal: "State portal",
};

function SignaturePad({ onChange, testid }) {
  const ref = useRef(null);
  const drawing = useRef(false);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0A0A0A";
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(ref.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={ref}
        width={520}
        height={110}
        data-testid={testid}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full border-2 border-ink bg-white touch-none cursor-crosshair"
      />
      <button onClick={clear} data-testid="signature-clear"
        className="mt-2 border-2 border-ink px-3 py-1 font-mono text-[10px] uppercase inline-flex items-center gap-1 hover:bg-ink hover:text-white">
        <Eraser className="w-3 h-3" /> Clear signature
      </button>
    </div>
  );
}

export default function QuarterlyFilingCenter({ year, enabled }) {
  const [filings, setFilings] = useState([]);
  const [open, setOpen] = useState(null);       // { quarter }
  const [preview, setPreview] = useState(null);
  const [deposits, setDeposits] = useState("0");
  const [deposits940, setDeposits940] = useState("0");
  const [signer, setSigner] = useState({ name: "", title: "", phone: "", signature_data_url: null });
  const [busy, setBusy] = useState(false);
  const [packetOpts, setPacketOpts] = useState({ include_940: true, include_state: true, state: "", ui_rate_pct: "", ui_registration: "", wt_payments: "" });
  const [validation, setValidation] = useState(null);

  const downloadPacket = async (quarter) => {
    setBusy("packet");
    const t = toast.loading(`Assembling Q${quarter} packet — 941${packetOpts.include_940 ? " + 940" : ""}${packetOpts.include_state ? " + state return" : ""}…`);
    try {
      const res = await api.post("/reports/quarterly-packet.pdf", {
        year: Number(year), quarter,
        deposits_941: Number(deposits || 0),
        deposits_940: Number(deposits940 || 0),
        include_940: packetOpts.include_940,
        include_state: packetOpts.include_state,
        state: packetOpts.state || "",
        ui_rate_pct: Number(packetOpts.ui_rate_pct || 0),
        ui_registration: packetOpts.ui_registration || "",
        wt_payments: Number(packetOpts.wt_payments || 0),
        signer,
      }, { responseType: "blob", timeout: 120000 });
      save(res.data, `quarterly-packet-Q${quarter}-${year}.pdf`, "application/pdf");
      const pages = res.headers?.["x-packet-pages"];
      toast.success(`Full Quarterly Packet · Q${quarter} ${year}${pages ? ` · ${pages} pages` : ""}`, { id: t });
    } catch (e) {
      const blob = e?.response?.data;
      let msg = formatApiError(e);
      if (blob instanceof Blob) {
        try {
          const d = JSON.parse(await blob.text()).detail;
          if (d && typeof d === "object") {
            msg = d.message || msg;
            if (Array.isArray(d.blockers) && d.blockers[0]) msg += ` First: ${d.blockers[0].message}`;
          } else if (d) { msg = d; }
        } catch { /* keep msg */ }
      }
      toast.error(msg, { id: t });
    }
    finally { setBusy(false); }
  };

  const loadFilings = useCallback(async () => {
    try {
      const { data } = await api.get(`/reports/filings?year=${year}`);
      setFilings(data.filings || []);
    } catch { setFilings([]); }
  }, [year]);
  useEffect(() => { loadFilings(); }, [loadFilings]);

  const byQuarter = useMemo(() => {
    const m = {};
    filings.forEach((f) => { if (f.form === "941") m[f.quarter] = f; });
    return m;
  }, [filings]);

  const openAction = async (quarter) => {
    setOpen({ quarter });
    setPreview(null);
    setDeposits("0");
    try {
      const { data } = await api.get(`/reports/941/preview?year=${year}&quarter=${quarter}`);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = (blob, filename, type) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([blob], { type }));
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const logFiling = async (quarter, method) => {
    try {
      await api.post("/reports/filings", { year: Number(year), quarter, form: "941", method });
      loadFilings();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadCsv = async (quarter) => {
    setBusy(true);
    try {
      const res = await api.get(`/reports/quarterly-tax.csv?year=${year}&quarter=${quarter}&format=irs_941`, { responseType: "blob" });
      save(res.data, `irs-941-efile-Q${quarter}-${year}.csv`, "text/csv");
      await logFiling(quarter, "e_file_csv");
      toast.success(`Q${quarter} e-file CSV downloaded — logged as e-filed`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (quarter) => {
    setBusy(true);
    try {
      const res = await api.post("/reports/941.pdf", {
        year: Number(year), quarter, deposits: Number(deposits || 0), signer,
      }, { responseType: "blob" });
      save(res.data, `irs-941-Q${quarter}-${year}.pdf`, "application/pdf");
      await logFiling(quarter, "signable_pdf");
      toast.success(`Form 941 · Q${quarter} ${year} generated`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const line = (label, value, strong = false) => (
    <div className={`flex justify-between gap-4 font-mono text-xs py-1 border-b border-ink/15 ${strong ? "font-bold" : ""}`}>
      <span>{label}</span><span>{typeof value === "number" ? fmtMoney(value) : value}</span>
    </div>
  );

  return (
    <div className="hard-card p-6 mb-8" data-testid="quarterly-filing-center">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> § DUAL-FILING ENGINE</div>
          <div className="font-heading font-black text-2xl tracking-tight">Form 941 Filing Center · {year}</div>
          <div className="font-mono text-xs text-muted-foreground mt-1 max-w-xl">
            Official IRS Form 941 filled on the government template (lines 1–15, plus Schedule B
            for semiweekly depositors), or the e-file CSV for a portal upload. Every action is logged below.
          </div>
        </div>
      </div>

      <div className="mt-5 border-2 border-ink overflow-x-auto">
        <table className="w-full text-sm" data-testid="filing-tracker-table">
          <thead>
            <tr className="border-b-2 border-ink">
              {["Quarter", "Period", "Filing status", "Action"].map((h) => (
                <th key={h} className="px-2 sm:px-3 py-2 text-left font-mono text-[10px] uppercase tracking-widest">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {QUARTERS.map((q) => {
              const f = byQuarter[q];
              return (
                <tr key={q} className="border-b border-ink/20 last:border-b-0" data-testid={`filing-row-q${q}`}>
                  <td className="px-2 sm:px-3 py-3 font-heading font-black">Q{q}</td>
                  <td className="px-2 sm:px-3 py-3 font-mono text-xs whitespace-nowrap">
                    {["Jan–Mar", "Apr–Jun", "Jul–Sep", "Oct–Dec"][q - 1]} {year}
                  </td>
                  <td className="px-2 sm:px-3 py-3 font-mono text-xs" data-testid={`filing-status-q${q}`}>
                    {f ? (
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {METHOD_LABEL[f.method] || f.method} on {f.filed_on}
                      </span>
                    ) : <span className="opacity-60">Not filed</span>}
                  </td>
                  <td className="px-2 sm:px-3 py-3">
                    <button
                      onClick={() => openAction(q)}
                      disabled={!enabled}
                      data-testid={`compliance-action-q${q}`}
                      className="border-2 border-ink px-2 sm:px-3 py-2 font-mono text-[10px] uppercase tracking-widest whitespace-nowrap hover:bg-ink hover:text-white disabled:opacity-40"
                    >
                      <span className="sm:hidden">File</span>
                      <span className="hidden sm:inline">Compliance action</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && createPortal(
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="compliance-action-modal">
          <div className="bg-white border-2 border-ink max-w-3xl w-full max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between border-b-2 border-ink p-4">
              <h3 className="font-black text-base md:text-lg">Form 941 · Q{open.quarter} {year}</h3>
              <button onClick={() => setOpen(null)} data-testid="compliance-action-close"
                className="border-2 border-ink px-3 py-1 font-mono text-xs uppercase inline-flex items-center gap-1">
                <X className="w-3 h-3" /> Close
              </button>
            </div>
            <div className="p-5 overflow-auto space-y-6">
              {!preview ? <p className="font-mono text-xs">COMPUTING LINES…</p> : (
                <>
                  <div data-testid="form941-lines">
                    <div className="overline mb-2">Part 1 · lines 1–15</div>
                    {line("1 · Employees paid", String(preview.line1_employees))}
                    {line("2 · Wages, tips, other compensation", preview.line2_wages)}
                    {line("3 · Federal income tax withheld", preview.line3_federal_withheld)}
                    {line("5a · Social security wages × 0.124", preview.line5a_ss_tax)}
                    {line("5c · Medicare wages × 0.029", preview.line5c_medicare_tax)}
                    {line("5d · Additional Medicare × 0.009", preview.line5d_addl_medicare_tax)}
                    {line("5e · Total SS + Medicare", preview.line5e_total_ss_medicare)}
                    {line("6 · Total taxes before adjustments", preview.line6_total_before_adjustments)}
                    {line("10 · Total after adjustments", preview.line10_total_after_adjustments)}
                    {line("12 · Total after credits", preview.line12_total_after_credits, true)}
                    {line("13 · Total deposits", preview.line13_total_deposits)}
                    {line("14 · Balance due", preview.line14_balance_due, true)}
                    {line("15a · Overpayment", preview.line15_overpayment)}
                    {line("Workers' Comp premium (employer)", preview.wc_premium_employer)}
                    <div className="font-mono text-[10px] mt-2 opacity-70" data-testid="deposit-schedule-note">
                      {preview.deposit_schedule === "semiweekly"
                        ? "Semiweekly depositor — Schedule B is required and is attached to both the standalone return and the full packet."
                        : "Monthly depositor — line 16 monthly liability is completed on the return. The full packet still carries Schedule B as a liability worksheet."}
                    </div>
                  </div>

                  <div className="border-2 border-ink p-4" data-testid="schedule-b-block">
                    <div className="overline mb-2">
                      § Schedule B · tax liability by month
                    </div>
                    <div className="font-mono text-xs space-y-1" data-testid="schedule-b-months">
                      {Object.entries(preview.monthly_liability || {}).map(([m, v], i) => (
                        <div key={m} className="flex justify-between" data-testid={`schedule-b-month-${i + 1}`}>
                          <span>Month {i + 1} · {String(year)}-{String(m).padStart(2, "0")}</span>
                          <span>{fmtMoney(v)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-black border-t-2 border-ink pt-1"
                        data-testid="schedule-b-quarter-total">
                        <span>Quarter total — must equal line 12</span>
                        <span>{fmtMoney(preview.quarter_liability)}</span>
                      </div>
                    </div>
                    <div className="font-mono text-[10px] mt-2 opacity-70" data-testid="schedule-b-reconciled">
                      {preview.daily_grid_reconciled
                        ? "Day grid reconciled — every day box sums to its month total, and the quarter total equals line 12."
                        : "Day grid out of balance — review the pay dates in this quarter before filing."}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <div className="overline mb-2">Deposits already made (line 13)</div>
                      <input
                        data-testid="form941-deposits"
                        type="number" step="0.01" min="0" value={deposits}
                        onChange={(e) => setDeposits(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-ink font-mono text-sm"
                      />
                      <div className="overline mt-4 mb-2">Signer</div>
                      <input data-testid="form941-signer-name" placeholder="Print name" value={signer.name}
                        onChange={(e) => setSigner({ ...signer, name: e.target.value })}
                        className="w-full px-3 py-2 border-2 border-ink font-mono text-sm mb-2" />
                      <input data-testid="form941-signer-title" placeholder="Title" value={signer.title}
                        onChange={(e) => setSigner({ ...signer, title: e.target.value })}
                        className="w-full px-3 py-2 border-2 border-ink font-mono text-sm mb-2" />
                      <input data-testid="form941-signer-phone" placeholder="Best daytime phone" value={signer.phone}
                        onChange={(e) => setSigner({ ...signer, phone: e.target.value })}
                        className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
                    </div>
                    <div>
                      <div className="overline mb-2">Digital signature — sign with mouse or finger</div>
                      <SignaturePad testid="form941-signature-pad"
                        onChange={(url) => setSigner((s) => ({ ...s, signature_data_url: url }))} />
                    </div>
                  </div>

                  <div className="border-2 border-ink p-4" data-testid="quarterly-packet-block">
                    <div className="overline mb-2 flex items-center gap-2">
                      <Layers className="w-4 h-4" /> § Full Quarterly Packet
                    </div>                    <div className="font-mono text-[11px] text-muted-foreground mb-3 leading-relaxed">
                      Merges Form 941 with Schedule B,
                      Form 940 and your state quarterly return (NY NYS-45 includes Part C) into one PDF
                      behind a contents page. Signer details and signature above are applied to every return.
                    </div>
                    <div className="mb-4">
                      <ComplianceValidation
                        year={year} quarter={open.quarter}
                        includeState={packetOpts.include_state}
                        state={packetOpts.state}
                        onStatus={setValidation}
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 mb-3">
                      <label className="flex items-center gap-2 font-mono text-[11px]">
                        <input type="checkbox" data-testid="packet-include-940"
                               checked={packetOpts.include_940}
                               onChange={(e) => setPacketOpts({ ...packetOpts, include_940: e.target.checked })} />
                        Include Form 940 (annual FUTA)
                      </label>
                      <label className="flex items-center gap-2 font-mono text-[11px]">
                        <input type="checkbox" data-testid="packet-include-state"
                               checked={packetOpts.include_state}
                               onChange={(e) => setPacketOpts({ ...packetOpts, include_state: e.target.checked })} />
                        Include state quarterly return
                      </label>
                      <label className="block">
                        <span className="block font-mono text-[10px] uppercase tracking-widest mb-1">940 deposits made</span>
                        <input data-testid="packet-deposits-940" type="number" step="0.01" min="0"
                               value={deposits940} onChange={(e) => setDeposits940(e.target.value)}
                               className="w-full px-2 py-1.5 border-2 border-ink font-mono text-xs" />
                      </label>
                      <label className="block">
                        <span className="block font-mono text-[10px] uppercase tracking-widest mb-1">State UI rate %</span>
                        <input data-testid="packet-ui-rate" type="number" step="0.01" min="0"
                               placeholder="e.g. 4.10" value={packetOpts.ui_rate_pct}
                               onChange={(e) => setPacketOpts({ ...packetOpts, ui_rate_pct: e.target.value })}
                               disabled={!packetOpts.include_state}
                               className="w-full px-2 py-1.5 border-2 border-ink font-mono text-xs disabled:bg-secondary/60" />
                      </label>
                    </div>
                    <button
                      onClick={() => downloadPacket(open.quarter)}
                      disabled={busy || (validation && !validation.ok)}
                      data-testid="download-quarterly-packet"
                      title={validation && !validation.ok
                        ? "Compliance Validation found blockers — fix them first"
                        : "Merge 941, 940 and the state return into one PDF"}
                      className="w-full bg-ink text-white border-2 border-ink px-4 py-4 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 hover:bg-white hover:text-ink transition-colors disabled:opacity-40"
                    ><Layers className="w-4 h-4" />
                      {busy === "packet" ? "Assembling packet…"
                        : (validation && !validation.ok) ? "Blocked — fix compliance issues"
                        : "Download Full Quarterly Packet"}</button>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <button
                      onClick={() => downloadCsv(open.quarter)} disabled={busy}
                      data-testid="download-efile-csv"
                      className="border-2 border-ink px-4 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 hover:bg-ink hover:text-white disabled:opacity-40"
                    ><Download className="w-4 h-4" /> Download e-file CSV</button>
                    <button
                      onClick={() => downloadPdf(open.quarter)} disabled={busy}
                      data-testid="generate-signable-pdf"
                      className="hard-btn px-4 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40"
                    ><PenLine className="w-4 h-4" /> {busy ? "Generating…" : "Generate signable PDF"}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
